import {
  Autocomplete,
  CircularProgress,
  Modal,
  TextField
} from '@mui/material';
import { actions } from '../../../../redux/store/store';
import { ErrorMessage, InputItem, Label } from '../../Form';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  useCreateBonusCampaignMutation,
  useUpdateBonusCampaignMutation
} from '../../../../api/bonusCampaign';
import { useEffect, useRef } from 'react';
import { sendSuccessMessage } from '../../../../constants/extras/sendSuccessMsg';
import { checkForError } from '../../../../constants/extras/errorHandlers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { autocomplete, errorAutoComplete } from '../../orderHelper/styles';
import { CampaignType, WhoGetPaid } from '../../../../constants/menuLists';
import { yupResolver } from '@hookform/resolvers/yup';
import { bonusCampaignValidationSchema } from '../../../../constants/validaitonSchema';
import { useSelector } from 'react-redux';
import { messageListOptionStyles } from '../email/optionStyles';
import MySelect, {
  animatedComponents,
  MenuList,
  MultiValue,
  Option,
  ValueContainer
} from '../../options/MessageOption';
import { handleBackgroundCheck } from '../../../../constants/extras/statusesBadge';
import moment from 'moment';

function convertDate(date) {
  return moment(date.substring(0, 10)).format("YYYY-MM-DD")
}

export default function BonusCampaignModel({ id: campaign }) {
  const { metroList } = useSelector((state) => state.campaign);

  const [createBonusCampaign, { isLoading: createLoading }] =
    useCreateBonusCampaignMutation();
  const [updateBonusCampaign, { isLoading: updateLoading }] =
    useUpdateBonusCampaignMutation();
  const form = useForm({
    defaultValues: {
      isActive: (campaign?.isExpired ? false : campaign?.isActive) ?? true,
      name: campaign?.name || '',
      type: campaign?.type || '',
      paymentEligibility: campaign?.paymentEligibility || '',
      startDate: campaign?.campaignDate?.startDate ? convertDate(campaign.campaignDate.startDate) : new Date(),
      endDate: campaign?.campaignDate?.endDate ? convertDate(campaign.campaignDate.endDate) : new Date(),
      amount: campaign?.amount || '',
      maxDays: campaign?.maxDays || '',
      routeDueDate: campaign?.routeDueDate ? convertDate(campaign.routeDueDate) : new Date(),
      minRoutes: campaign?.minRoutes || '',
      metros: []
    },
    resolver: yupResolver(bonusCampaignValidationSchema)
  });

  const { setValue, setError } = form;
  // Watch form fields including type to conditionally render fields
  const { startDate, endDate, metros, type } = useWatch({ control: form.control });
  const isExpired = useRef(campaign?.isExpired);

  useEffect(() => {
    if (metroList) {
      const selectedMetro = campaign?.metros?.map((item) =>
        metroList.find((metro) => metro.value === item)
      );
      setValue('metros', selectedMetro);
    }
  }, [metroList, setValue, campaign]);

  useEffect(() => {
    if (isExpired.current) {
      const endDateValue = new Date(endDate);
      endDateValue.setHours(23, 59, 59, 999);
      if (endDateValue > new Date()) {
        isExpired.current = false;
        setValue('isActive', true);
      }
    }
  }, [endDate, setValue]);

  const onCancel = () => {
    actions.modal.closeCampaign();
  };

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      metros:
        metros?.map((item) => item?.value)?.filter((value) => value !== '*') ||
        [],
      campaignDate: {
        startDate: moment(data.startDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        endDate: moment(data.endDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
      },
      routeDueDate: moment(data.routeDueDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
      startDate: undefined,
      endDate: undefined
    };

    if(data.type === 'Referral' && !data.paymentEligibility){
      setError('paymentEligibility', {
        type: 'manual',
        message: 'Please select who gets paid'
      });
      return;
    }
    
    if(data.routeDueDate < data.endDate) {
      setError('routeDueDate', {
        type: 'manual',
        message: 'Route due date cannot be before end date'
      });
      return;
    }

    const { data: result } = campaign?._id
      ? await updateBonusCampaign({
        id: campaign._id,
        body: payload
      })
      : await createBonusCampaign({ body: payload });

    if (!result?.error) {
      sendSuccessMessage(
        `Bonus Program ${campaign?._id ? 'Updated' : 'Created'} Successfully`
      );
      onCancel();
    } else {
      checkForError(result);
    }
  };

  return (
    <Modal
      open
      onClose={onCancel}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div className={`modal after-loading`} style={{ maxWidth: '30rem' }}>
        <div className="modal__header">
          <h2 className="modal__title">
            {campaign ? 'Update Campaign' : 'New Campaign'}
          </h2>
        </div>
        <div className="modal__body">
          <form>
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '1rem'
              }}
            >
              <InputItem
                name="name"
                form={form}
                title="Name"
                autoComplete="off"
              />
              <span style={{ width: '50%' }}>
                <Label title="Type" />
                <Controller
                  control={form.control}
                  name="type"
                  render={({
                    field: { onChange, value, ref },
                    fieldState: { error }
                  }) => {
                    const styleObj = error
                      ? { ...errorAutoComplete }
                      : { ...autocomplete };
                    return (
                      <>
                        <Autocomplete
                          noOptionsText={'Campaign Type'}
                          options={CampaignType}
                          value={value || ''}
                          getOptionLabel={(option) => option}
                          sx={styleObj}
                          onChange={(e, data) => {
                            onChange(data);
                          }}
                          blurOnSelect={true}
                          ref={ref}
                          isAll={true}
                          isError={error && true}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Campaign Type"
                              inputProps={{
                                ...params.inputProps
                              }}
                              required={false}
                            />
                          )}
                        />
                        <ErrorMessage error={error} />
                      </>
                    );
                  }}
                />
              </span>
            </div>
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '1rem'
              }}
            >
              <span>
                <Label title="Campaign Start Date" />
                <Controller
                  control={form.control}
                  name="startDate"
                  render={({
                    field: { onChange, value },
                    fieldState: { error }
                  }) => (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        minDate={
                          campaign?._id
                            ? moment(convertDate(campaign?.campaignDate?.startDate)).toDate()
                            : new Date()
                        }
                        value={moment(value).toDate()}
                        onChange={(date) => {
                          onChange(date);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            style={{ width: '100%' }}
                            id="date-picker"
                          />
                        )}
                      />
                      <ErrorMessage error={error} />
                    </LocalizationProvider>
                  )}
                />
              </span>
              <span>
                <Label title="Campaign End Date" />
                <Controller
                  control={form.control}
                  name="endDate"
                  render={({
                    field: { onChange, value },
                    fieldState: { error }
                  }) => (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        minDate={moment(startDate).toDate() || undefined}
                        value={moment(value).toDate()}
                        onChange={(date) => {
                          onChange(date);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            style={{ width: '100%' }}
                            id="date-picker"
                          />
                        )}
                      />
                      <ErrorMessage error={error} />
                    </LocalizationProvider>
                  )}
                />
              </span>
            </div>
            <div style={{ position: 'relative', padding: '1rem 0' }}>
              <Label title="Metros" />
              <MySelect
                placeholder="Select Metros"
                options={metroList}
                isMulti={true}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                value={metros}
                onChange={(value) => {
                  setValue('metros', value);
                }}
                allowSelectAll={true}
                styles={messageListOptionStyles}
                components={{
                  MenuList,
                  Option,
                  ValueContainer,
                  animatedComponents,
                  MultiValue,
                  IndicatorSeparator: () => null,
                  LoadingIndicator: () => null
                }}
                noOptionsMessage={() => 'No Metro available'}
                isLoading={false}
                isDisabled={false}
              />
            </div>
            {type === 'Referral' && (
              <div style={{ position: 'relative' }}>
                <Label title="Who Gets Paid" />
                <Controller
                  control={form.control}
                  name="paymentEligibility"
                  render={({
                    field: { onChange, value, ref },
                    fieldState: { error }
                  }) => {
                    const styleObj = error
                      ? { ...errorAutoComplete }
                      : { ...autocomplete };
                    return (
                      <>
                        <Autocomplete
                          noOptionsText={'Who Gets Paid'}
                          options={WhoGetPaid}
                          value={value || ''}
                          getOptionLabel={(option) => option}
                          sx={styleObj}
                          onChange={(e, data) => {
                            onChange(data);
                          }}
                          blurOnSelect={true}
                          ref={ref}
                          isAll={true}
                          isError={error && true}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Who Gets Paid"
                              inputProps={{
                                ...params.inputProps
                              }}
                              required={false}
                            />
                          )}
                        />
                        <ErrorMessage error={error} />
                      </>
                    );
                  }}
                />
              </div>
            )}
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '1rem'
              }}
            >
              <span style={{ width: '60%' }}>
                <InputItem
                  name="amount"
                  form={form}
                  title="Amount"
                  autoComplete="off"
                  numeric
                />
              </span>
              <InputItem
                name="minRoutes"
                form={form}
                title="Route Threshold"
                autoComplete="off"
                numeric
              />
            </div>
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '1rem'
              }}
            >
              <span style={{ width: '50%' }}>
                <Label title="Route due date" />
                <Controller
                  control={form.control}
                  name="routeDueDate"
                  render={({
                    field: { onChange, value },
                    fieldState: { error }
                  }) => (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        minDate={moment(endDate).toDate() || undefined}
                        value={moment(value).toDate()}
                        onChange={(date) => {
                          onChange(date);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            style={{ width: '100%' }}
                            id="date-picker"
                          />
                        )}
                      />
                      <ErrorMessage error={error} />
                    </LocalizationProvider>
                  )}
                />
              </span>
              <div
                style={{
                  width: '50%',
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Label title="Status" />
                {!isExpired.current ? (
                  <Controller
                    control={form.control}
                    name="isActive"
                    render={({ field: { onChange, value } }) => (
                      <div className="toggle_container_metros pl-2">
                        <div className="toggle-switch-metros">
                          <input
                            type="checkbox"
                            className="checkbox"
                            name="isActive"
                            id="isActive"
                            checked={value}
                            onChange={(e) => {
                              onChange(e.target.checked);
                            }}
                          />
                          <label className="label" htmlFor="isActive">
                            <span className="backgroundC" />
                            <span className="switch-metros" />
                          </label>
                        </div>
                      </div>
                    )}
                  />
                ) : (
                  <span
                    className="textStyleForStatus"
                    style={handleBackgroundCheck(false)}
                  >
                    Expired
                  </span>
                )}
              </div>
            </div>
          </form>
        </div>
        <div className="modal__footer">
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
          <div style={{ position: 'relative' }}>
            <button
              className={`btn btn_accent ${createLoading || updateLoading ? 'with-progress' : ''
                }`}
              disabled={createLoading || updateLoading}
              onClick={form.handleSubmit(onSubmit)}
            >
              {campaign?._id ? 'Update' : 'Save'}
            </button>
            {(createLoading || updateLoading) && (
              <CircularProgress
                size={18}
                sx={{
                  color: '#fff',
                  position: 'absolute',
                  top: '50%',
                  left: '83%',
                  marginTop: '-8px',
                  marginLeft: '-10px'
                }}
              />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
